import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Bell, X, Repeat, Clock, Plus, Pause, Play, Trash2, BellRing } from "lucide-react";
import { toast } from "sonner";
import { PushSettings } from "./PushSettings";

const POLL_MS = 15000;

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; o.type = "sine";
    g.gain.setValueAtTime(0.06, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    o.start(); o.stop(ctx.currentTime + 0.4);
  } catch { /* audio not available */ }
}

function desktopNotify(title, body) {
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      const n = new Notification(`⏰ ${title}`, { body: body || "", icon: "/ito-logo.png", tag: title });
      n.onclick = () => { window.focus(); n.close(); };
    }
  } catch { /* ignore */ }
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [timers, setTimers] = useState([]);
  const [due, setDue] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", mode: "recurring", value: 10, unit: "minutes" });
  const seen = useRef(new Set());
  const panelRef = useRef(null);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  async function enableDesktop() {
    if (typeof Notification === "undefined") { toast.error("Desktop notifications aren't supported in this browser"); return; }
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
      if (p === "granted") { toast.success("Desktop alerts on"); desktopNotify("Desktop alerts enabled", "You'll get a popup when a reminder fires."); }
      else toast("Desktop alerts blocked — enable them in your browser's site settings");
    } catch { /* ignore */ }
  }

  const loadTimers = useCallback(async () => {
    try { const { data } = await api.get("/timers"); setTimers(data || []); }
    catch { /* ignore */ }
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/timers/due");
      setDue(data || []);
      (data || []).forEach((d) => {
        const key = `${d.id}:${d.last_fired_at}`;
        if (!seen.current.has(key)) {
          seen.current.add(key);
          beep();
          toast(`⏰ ${d.title}`, { description: d.note || d.description, duration: 8000 });
          desktopNotify(d.title, d.note || d.description);
        }
      });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    const onChanged = () => { poll(); loadTimers(); };
    window.addEventListener("timers-changed", onChanged);
    return () => { clearInterval(id); window.removeEventListener("timers-changed", onChanged); };
  }, [poll, loadTimers]);

  useEffect(() => { if (open) loadTimers(); }, [open, loadTimers]);

  useEffect(() => {
    function onClick(e) { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function ack(t) {
    try { await api.post(`/timers/${t.id}/ack`); setDue((d) => d.filter((x) => x.id !== t.id)); }
    catch { toast.error("Could not dismiss"); }
  }
  async function snooze(t, mins) {
    try { await api.patch(`/timers/${t.id}`, { snooze_minutes: mins }); setDue((d) => d.filter((x) => x.id !== t.id)); toast.success(`Snoozed ${mins} min`); loadTimers(); }
    catch { toast.error("Could not snooze"); }
  }
  async function toggleActive(t) {
    try { await api.patch(`/timers/${t.id}`, { active: !t.active }); loadTimers(); }
    catch { toast.error("Could not update"); }
  }
  async function removeTimer(t) {
    try { await api.delete(`/timers/${t.id}`); setTimers((x) => x.filter((y) => y.id !== t.id)); setDue((d) => d.filter((x) => x.id !== t.id)); }
    catch { toast.error("Could not delete"); }
  }
  async function addTimer(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    const val = Math.max(1, Number(form.value) || 1);
    const minutes = form.unit === "hours" ? val * 60 : val;
    const body = form.mode === "recurring"
      ? { title: form.title, interval_minutes: minutes }
      : { title: form.title, in_minutes: minutes };
    try {
      await api.post("/timers", body);
      toast.success("Reminder set");
      setForm({ title: "", mode: "recurring", value: 10, unit: "minutes" });
      setShowAdd(false);
      loadTimers();
    } catch { toast.error("Could not set reminder"); }
  }

  const dueCount = due.length;

  return (
    <div className="relative" ref={panelRef}>
      <button onClick={() => setOpen((o) => !o)} data-testid="notification-bell"
        className="relative text-[#68665E] hover:text-[#C84B31] transition-colors p-1" title="Reminders & alerts">
        {dueCount > 0 ? <BellRing className="w-5 h-5 text-[#C84B31]" /> : <Bell className="w-5 h-5" />}
        {dueCount > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-[#DA1E28] text-white text-[10px] font-semibold">
            {dueCount > 9 ? "9+" : dueCount}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel"
          className="absolute right-0 mt-2 w-[340px] max-h-[70vh] overflow-y-auto bg-white border border-[#E5E3DB] rounded-lg shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E3DB] sticky top-0 bg-white">
            <div className="font-headings font-bold text-[#1A1A18] text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-[#C84B31]" /> Reminders</div>
            <button onClick={() => setShowAdd((s) => !s)} className="text-[#C84B31] hover:opacity-80" data-testid="notification-add-toggle" title="New reminder"><Plus className="w-4 h-4" /></button>
          </div>

          {notifPerm !== "granted" && notifPerm !== "unsupported" && false && (
            <button onClick={enableDesktop} data-testid="notification-enable-desktop" className="hidden">enable</button>
          )}
          <PushSettings />

          {showAdd && (
            <form onSubmit={addTimer} className="p-3 border-b border-[#E5E3DB] bg-[#FDFCF8] space-y-2" data-testid="notification-add-form">
              <input className="input-field !py-1.5 text-sm" placeholder="Remind me to…" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notification-title-input" />
              <div className="flex items-center gap-2">
                <select className="input-field !py-1.5 text-sm flex-1" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="notification-mode-select">
                  <option value="recurring">Every</option>
                  <option value="once">In</option>
                </select>
                <input type="number" min="1" className="input-field !py-1.5 text-sm w-16" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="notification-value-input" />
                <select className="input-field !py-1.5 text-sm" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="notification-unit-select">
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                </select>
              </div>
              <button type="submit" className="btn-primary w-full !py-1.5 text-sm" data-testid="notification-save-btn">Set reminder</button>
            </form>
          )}

          {dueCount > 0 && (
            <div className="p-2 space-y-2 border-b border-[#E5E3DB]">
              <div className="text-[10px] uppercase tracking-wide text-[#68665E] px-2">Ringing now</div>
              {due.map((t) => (
                <div key={t.id} className="bg-[#FBEEEA] border border-[#F0CFC5] rounded p-2.5" data-testid={`notification-due-${t.id}`}>
                  <div className="font-medium text-[#1A1A18] text-sm flex items-center gap-1.5"><BellRing className="w-3.5 h-3.5 text-[#C84B31]" /> {t.title}</div>
                  {t.note && <div className="text-xs text-[#68665E] mt-0.5">{t.note}</div>}
                  <div className="text-[11px] text-[#68665E] mt-0.5">{t.description}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => ack(t)} className="text-xs px-2 py-1 rounded bg-[#C84B31] text-white" data-testid={`notification-dismiss-${t.id}`}>Dismiss</button>
                    <button onClick={() => snooze(t, 10)} className="text-xs px-2 py-1 rounded bg-[#E5E3DB] text-[#4A4842]" data-testid={`notification-snooze-${t.id}`}>Snooze 10m</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="p-2 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wide text-[#68665E] px-2 pt-1">Your reminders</div>
            {timers.length === 0 ? (
              <div className="text-xs text-[#68665E] px-2 py-3" data-testid="notification-empty">No reminders yet. Add one, or ask GARV “remind me to check email every 10 minutes”.</div>
            ) : timers.map((t) => (
              <div key={t.id} className={`flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F5F4F0] ${t.active ? "" : "opacity-60"}`} data-testid={`notification-timer-${t.id}`}>
                {t.kind === "recurring" ? <Repeat className="w-3.5 h-3.5 text-[#C84B31] shrink-0" /> : <Clock className="w-3.5 h-3.5 text-[#68665E] shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#1A1A18] truncate">{t.title}</div>
                  <div className="text-[11px] text-[#68665E]">{t.description}{!t.active ? " · paused" : ""}{t.link_label ? ` · ${t.link_label}` : ""}{t.auto_stop ? " · until done" : ""}</div>
                </div>
                <button onClick={() => toggleActive(t)} className="text-[#68665E] hover:text-[#C84B31]" title={t.active ? "Pause" : "Resume"} data-testid={`notification-toggle-${t.id}`}>
                  {t.active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => removeTimer(t)} className="text-[#8A2526] hover:opacity-80" title="Delete" data-testid={`notification-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
